/**
 * Funnel × FYP: a funnel version that shows short videos from the store's FYP
 * in place of a sales page — one video on its own, or every video of one post
 * in its order — with or without the page's own sales page after the last one.
 * The choice lives in funnel_variants.source (and funnel_drafts.source), so a
 * draft, a start, a pause or a retirement carries it like any other version:
 *
 *   fyp:v:<video id>[:sp]   one video, no scrolling to others
 *   fyp:p:<post id>[:sp]    every published video of the post
 *
 * `:sp` = the sales page follows the last video. The version's slug is the
 * feed's address behind "@", which no imported copy can be called, so nothing
 * that reads a copy by slug (previews, heatmaps, the AI) mistakes it for one.
 * Pure: the Worker, the console and the storefront share it.
 */
import type { FypVideoKind, PublicFyp } from "./types";
import { fypShopPath } from "./fypProfile";
import { liveCheckoutUrl } from "./liveBag";

export type FypSourceKind = "video" | "post";

export interface FypSource {
  kind: FypSourceKind;
  id: string;
  /** The page's sales page follows the last video. */
  sales: boolean;
}

const SOURCE_RE = /^fyp:(v|p):([A-Za-z0-9-]{8,64})(:sp)?$/;

export function parseFypSource(source: string | null | undefined): FypSource | null {
  const m = source ? SOURCE_RE.exec(source) : null;
  return m ? { kind: m[1] === "v" ? "video" : "post", id: m[2], sales: !!m[3] } : null;
}

export const isFypSource = (source: string | null | undefined): boolean => parseFypSource(source) != null;

export function fypSourceId(s: FypSource): string {
  return `fyp:${s.kind === "video" ? "v" : "p"}:${s.id}${s.sales ? ":sp" : ""}`;
}

/** A FYP version's slug: the feed's address behind "@". */
export const fypVersionSlug = (feedSlug: string): string => `@${feedSlug}`;

/** The first line of a caption, short enough for a block's title. */
export function captionTitle(caption: string, max = 40): string {
  const line = caption.split(/\r?\n/).map((l) => l.trim()).find(Boolean) ?? "";
  if (!line) return "Tanpa kapsyen";
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

/**
 * How a FYP version is named on the board: "FYP · <caption>", the number of
 * videos for a post, which video for one of several, and the sales page after
 * when there is one.
 */
export function fypVersionLabel(s: FypSource, caption: string, videos: number, position?: number): string {
  const what = s.kind === "post" ? ` · ${videos} video` : videos > 1 && position != null ? ` · video ${position + 1}` : "";
  return `FYP · ${captionTitle(caption)}${what}${s.sales ? " → sales page" : ""}`;
}

/** A FYP version's line under its name on the board. */
export function fypVersionSub(s: FypSource): string {
  return `${s.kind === "post" ? "Post FYP · semua video" : "Video FYP · satu video"} · ${s.sales ? "lepas itu sales page" : "tanpa sales page"}`;
}

/** What the storefront gets for a FYP version: the feed's address and the videos, in order. */
export interface PublicFunnelFyp {
  slug: string;
  codes: string[];
  /** The page's sales page follows the last video. */
  end: boolean;
}

/** One video of a post the console can make a version of, with its last 30 days on the FYP. */
export interface FunnelFypVideo {
  id: string;
  code: string;
  kind: FypVideoKind;
  youtubeId: string | null;
  poster: string | null;
  durationMs: number | null;
  views: number;
  bagOpens: number;
  buyClicks: number;
  paid: number;
  revenue: number;
  currency: string;
}

/** A FYP post the console can make a version of. */
export interface FunnelFypPost {
  id: string;
  caption: string;
  /** Only a published post's videos can be shown. */
  published: boolean;
  videos: FunnelFypVideo[];
  /** Bag products the funnel's page does not sell: their orders are not the funnel's. */
  offPage: string[];
}

/** The store's FYP for the console's picker: its address, or null with no feed yet. */
export interface FunnelFypOptions {
  slug: string | null;
  posts: FunnelFypPost[];
}

/**
 * The store's FYP as plain version choices, for a list that names sources the
 * way pages are named: each published post of several videos as a whole, then
 * each of its videos alone (a post of one video is just that video), every one
 * with and without the sales page after.
 */
export function fypSourceOptions(o: FunnelFypOptions | null | undefined): { id: string; slug: string; versiB: false; label: string; sub: string }[] {
  if (!o?.slug) return [];
  const slug = fypVersionSlug(o.slug);
  const out: { id: string; slug: string; versiB: false; label: string; sub: string }[] = [];
  const both = (kind: FypSourceKind, id: string, caption: string, n: number, at?: number) => {
    for (const sales of [true, false]) {
      const s: FypSource = { kind, id, sales };
      out.push({ id: fypSourceId(s), slug, versiB: false, label: fypVersionLabel(s, caption, n, at), sub: fypVersionSub(s) });
    }
  };
  for (const p of o.posts) {
    if (!p.published || !p.videos.length) continue;
    if (p.videos.length > 1) both("post", p.id, p.caption, p.videos.length);
    p.videos.forEach((v, i) => both("video", v.id, p.caption, p.videos.length, i));
  }
  return out;
}

/** On a checkout URL: the funnel and version the order is for (`<funnel id>.<version>`). */
export const FUNNEL_TAG_PARAM = "shz_ab";
/** On a checkout URL: opened in a sheet, so a checkout on its own whatever the page's style. */
export const SHEET_PARAM = "shz_sheet";
/** What a checkout in a sheet posts to the page around it when the buyer goes back. */
export const SHEET_CLOSE = "shz-sheet-close";
/** What a product page in a FYP sheet posts to have the whole screen (its checkout
 *  or cart sheet is open: {size: "full"}) and to give it back ({size: "auto"}). */
export const SHEET_SIZE = "shz-sheet-size";
/** What a product page in a FYP sheet posts with how tall its photo, price, name and
 *  sold count are over its buy bar ({px}): the sheet opens to exactly that. */
export const SHEET_FIT = "shz-sheet-fit";
/** On a product page's URL: opened over a funnel's video, so its Beli goes back to
 *  the funnel's own checkout (which credits the version) instead of the store's. */
export const SHEET_BUY_PARAM = "shz_buy";
/** What that product page posts to the page around it when the buyer taps Beli. */
export const SHEET_BUY = "shz-sheet-buy";
/** On a checkout URL in a sheet ("tab"): the gateway opens in a tab of its own, so
 *  the page around the sheet (a live class) keeps playing in this one. */
export const SHEET_PAY_PARAM = "shz_pay";
/** What that checkout posts to the page around it once the order is made. */
export const SHEET_PAY = "shz-sheet-pay";

/** The order a sheet's checkout made, and the gateway's address. `opened`: its tab
 *  is already on the gateway; otherwise the browser refused it and the buyer
 *  opens it with one more tap. */
export type SheetPayNotice = { url: string; orderId: string | null; opened: boolean };

/** A SHEET_PAY message as the page around the sheet trusts it: only an http(s)
 *  address, since it becomes a link the buyer taps. */
export function readSheetPay(data: unknown): SheetPayNotice | null {
  if (!data || typeof data !== "object") return null;
  const d = data as { type?: unknown; url?: unknown; orderId?: unknown; opened?: unknown };
  if (d.type !== SHEET_PAY || typeof d.url !== "string") return null;
  let url: URL;
  try {
    url = new URL(d.url);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  return { url: url.href, orderId: typeof d.orderId === "string" && d.orderId ? d.orderId : null, opened: d.opened === true };
}

/** A checkout held in a sheet whose gateway opens in its own tab. */
export function payInTabSheetUrl(src: string): string {
  const url = new URL(src, "https://sheet.invalid");
  url.searchParams.set(SHEET_PARAM, "1");
  url.searchParams.set(SHEET_PAY_PARAM, "tab");
  return url.pathname + url.search;
}

const TAG_RE = /^([a-z0-9]{8,24})\.([abcd])$/;

export function parseFunnelTag(v: string | null | undefined): { abTest: string; abArm: "a" | "b" | "c" | "d" } | null {
  const m = v ? TAG_RE.exec(v) : null;
  return m ? { abTest: m[1], abArm: m[2] as "a" | "b" | "c" | "d" } : null;
}

const CARRIED = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "__store", "__domain"] as const;

/**
 * Where Beli takes a viewer of a funnel's FYP version: the funnel page's own
 * checkout for that product, naming the video (the FYP's own numbers), the
 * funnel and version (the order is the version's even where this browser keeps
 * no storage) and the sheet. The ad's utm_* and fbclid ride along as the landing
 * had them — nothing is made up for a visit without them, so the order sits in
 * the same campaign row as the visit.
 */
export function funnelCheckoutUrl(base: string, addToCart: string, code: string, tag: { t: string; a: string }, pageSearch: string): string {
  const url = new URL(base, "https://funnel.invalid");
  url.searchParams.set("add-to-cart", addToCart);
  const from = new URLSearchParams(pageSearch);
  for (const k of CARRIED) {
    const v = from.get(k);
    if (v) url.searchParams.set(k, v);
  }
  url.searchParams.set("fyp", code);
  url.searchParams.set(FUNNEL_TAG_PARAM, `${tag.t}.${tag.a}`);
  url.searchParams.set(SHEET_PARAM, "1");
  return url.pathname + url.search;
}

/**
 * Where a funnel version's product opens when its post sends Lihat to the
 * product's page: the store's own single-product page on the store's domain, or
 * the FYP profile's on the platform's — marked so its Beli hands the purchase
 * back to the funnel's checkout. The ad's campaign rides along for its pixel.
 */
export function funnelProductUrl(productSlug: string, fypSlug: string, onStoreHost: boolean, code: string, pageSearch: string): string {
  const url = new URL(onStoreHost ? `/product/${encodeURIComponent(productSlug)}` : fypShopPath(fypSlug, productSlug), "https://funnel.invalid");
  const from = new URLSearchParams(pageSearch);
  for (const k of CARRIED) {
    const v = from.get(k);
    if (v) url.searchParams.set(k, v);
  }
  url.searchParams.set("fyp", code);
  url.searchParams.set(SHEET_BUY_PARAM, "1");
  return url.pathname + url.search;
}

/**
 * Where a live's product opens over the live: the same page a FYP video opens,
 * in the store's FYP Shop, marked as a sale from this live, and its Beli handed
 * back to the live's own checkout (the page's, so the order is the live's).
 */
export function liveProductUrl(productSlug: string, fypSlug: string, liveSlug: string): string {
  const url = new URL(liveCheckoutUrl(fypShopPath(fypSlug, productSlug), liveSlug), "https://live.invalid");
  url.searchParams.set(SHEET_BUY_PARAM, "1");
  return url.pathname + url.search;
}

/** The videos a FYP version shows, in order: the one video, or the post's own order. */
export function fypVersionCodes(s: FypSource, videos: { id: string; postId: string; code: string; position: number }[]): string[] {
  if (s.kind === "video") return videos.filter((v) => v.id === s.id).map((v) => v.code).slice(0, 1);
  return videos
    .filter((v) => v.postId === s.id)
    .sort((a, b) => a.position - b.position)
    .map((v) => v.code);
}

// ---- At the edge: the Worker picks a FYP funnel's version before the page is sent ----

/** Every address a funnel's page answers on ("<host><path>"), with the page's slug and team handle. */
export interface FunnelEntry {
  keys: string[];
  slug: string;
  handle: string | null;
  /** The page's Meta pixel (its own, else the store's; null with tracking off): its PageView goes out with the page. */
  px?: string | null;
}

const PIXEL_RE = /^\d{6,20}$/;

/** "<host><path>": the host in lower case, the path without a trailing slash (the root stays "/"). */
export function entryKey(host: string, path: string): string {
  const p = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return `${host.toLowerCase()}${p || "/"}`;
}

/**
 * Where a funnel's page is opened: its store's domain — at the root for the
 * store's home page, which redirects every other address of it there; else at
 * /<slug> and the /p/<slug> alias, or /<handle>/<slug> — and on the platform's
 * own host at /p/<slug> (or /<handle>/<slug>).
 */
export function funnelEntry(o: {
  apexHost: string | null;
  domain: string | null;
  home: boolean;
  slug: string;
  handle: string | null;
  pixel?: string | null;
}): FunnelEntry {
  const own = o.handle ? `/${o.handle}/${o.slug}` : `/${o.slug}`;
  const keys: string[] = [];
  if (o.domain) {
    if (o.home) keys.push(entryKey(o.domain, "/"));
    else keys.push(entryKey(o.domain, own), ...(o.handle ? [] : [entryKey(o.domain, `/p/${o.slug}`)]));
  }
  if (o.apexHost) keys.push(entryKey(o.apexHost, o.handle ? own : `/p/${o.slug}`));
  return { keys: [...new Set(keys)], slug: o.slug, handle: o.handle, px: o.pixel && PIXEL_RE.test(o.pixel) ? o.pixel : null };
}

export function parseFunnelEntry(raw: string | null | undefined): FunnelEntry | null {
  if (!raw) return null;
  try {
    const e = JSON.parse(raw) as Partial<FunnelEntry>;
    if (!Array.isArray(e.keys) || !e.keys.every((k) => typeof k === "string") || typeof e.slug !== "string") return null;
    return {
      keys: e.keys,
      slug: e.slug,
      handle: typeof e.handle === "string" ? e.handle : null,
      px: typeof e.px === "string" && PIXEL_RE.test(e.px) ? e.px : null,
    };
  } catch {
    return null;
  }
}

/** The cookie holding a visitor's version of one funnel: `<version>.<when picked, ms>`. */
export const funnelCookieName = (funnelId: string): string => `shz_ab_${funnelId}`;
/** As long as the browser's own record of it (FUNNEL_STORE_TTL_MS). */
export const FUNNEL_COOKIE_MAX_AGE = 60 * 86_400;

export function readArmCookie(header: string | null | undefined, funnelId: string): { arm: "a" | "b" | "c" | "d"; at: number } | null {
  if (!header) return null;
  const name = funnelCookieName(funnelId);
  for (const part of header.split(";")) {
    const i = part.indexOf("=");
    if (i < 0 || part.slice(0, i).trim() !== name) continue;
    const m = /^([abcd])\.(\d{10,16})$/.exec(part.slice(i + 1).trim());
    return m ? { arm: m[1] as "a" | "b" | "c" | "d", at: Number(m[2]) } : null;
  }
  return null;
}

/**
 * What the Worker writes into the page (#funnel-boot) when it picked the
 * visitor's version: the funnel, the version and when it was picked, the page
 * it was picked on (slug and handle, so the page can be read again), and for a
 * FYP version its videos and the feed itself, so they play before any request.
 */
export interface FunnelBoot {
  f: string;
  a: "a" | "b" | "c" | "d";
  at: number;
  page: string;
  slug: string;
  handle: string | null;
  path: string;
  /** The page's Meta pixel, so the video's PageView (LPV) need not wait for the page to be read. */
  px?: string;
  fyp?: PublicFunnelFyp;
  feed?: PublicFyp;
}

export function parseFunnelBoot(raw: unknown): FunnelBoot | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Partial<FunnelBoot>;
  if (typeof b.f !== "string" || !/^[a-z0-9]{8,24}$/.test(b.f)) return null;
  if (b.a !== "a" && b.a !== "b" && b.a !== "c" && b.a !== "d") return null;
  if (typeof b.at !== "number" || typeof b.page !== "string" || typeof b.slug !== "string" || typeof b.path !== "string") return null;
  const fyp = b.fyp && typeof b.fyp.slug === "string" && Array.isArray(b.fyp.codes) && b.fyp.codes.length > 0 ? b.fyp : undefined;
  const feed = fyp && b.feed && Array.isArray(b.feed.videos) && b.feed.videos.length > 0 ? b.feed : undefined;
  const px = typeof b.px === "string" && PIXEL_RE.test(b.px) ? b.px : undefined;
  return {
    f: b.f,
    a: b.a,
    at: b.at,
    page: b.page,
    slug: b.slug,
    handle: typeof b.handle === "string" ? b.handle : null,
    path: b.path,
    ...(px ? { px } : {}),
    ...(fyp ? { fyp } : {}),
    ...(feed ? { feed } : {}),
  };
}
